
import { useState, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { useGlobalCache, createSupabaseSubscription } from "@/hooks/use-global-cache";
import { useToast } from "@/hooks/use-toast";
import { Client } from "@/lib/supabase";
import { deleteClientData, createClient } from "@/lib/clients";
import { clientSchema } from "@/schemas/client-schema"; // Importiamo lo schema del cliente

export type { ClientFormValues } from "@/schemas/client-schema"; // Esportiamo il tipo ClientFormValues
export { clientSchema }; // Esportiamo lo schema del cliente

export function useClients() {
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();

  const { 
    data: clients = [], 
    isLoading,
    refresh: refreshClients,
    error 
  } = useGlobalCache<Client[]>(
    'all_clients',
    async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('last_name', { ascending: true });
      
      if (error) {
        console.error('Errore nel recupero dei clienti:', error);
        throw error;
      }
      
      return data || [];
    },
    {
      validity: 24 * 60 * 60 * 1000, // 24 ore di cache
      staleTime: 24 * 60 * 60 * 1000,
      setupRealtimeSubscription: (notifyUpdate) => {
        return createSupabaseSubscription(
          supabase,
          'clients',
          {},
          notifyUpdate
        );
      }
    }
  );

  // Assicuriamoci sempre che clients sia un array
  const filteredClients = useMemo(() => {
    // Utilizziamo una guardia per garantire che clients sia un array
    const clientsArray = Array.isArray(clients) ? clients : [];
    
    return clientsArray.filter((client) => {
      const searchValue = searchTerm.toLowerCase();
      const fullName = `${client.first_name || ''} ${client.last_name || ''}`.toLowerCase();
      const email = (client.email || '').toLowerCase();
      return fullName.includes(searchValue) || email.includes(searchValue);
    });
  }, [clients, searchTerm]);

  const saveClient = async (clientData: Partial<Client>) => {
    try {
      const data = await createClient(clientData);
      if (!data) throw new Error("Errore nella creazione del cliente");
      
      refreshClients();
      return true;
    } catch (error) {
      console.error('Errore nel salvataggio del cliente:', error);
      toast({
        title: "Errore",
        description: "Impossibile salvare il cliente",
        variant: "destructive"
      });
      return false;
    }
  };

  const deleteClient = async (clientId: string) => {
    try {
      const success = await deleteClientData(clientId);
      if (!success) throw new Error("Errore nell'eliminazione del cliente");
      
      refreshClients();
      return true;
    } catch (error) {
      console.error('Errore nell\'eliminazione del cliente:', error);
      toast({
        title: "Errore",
        description: "Impossibile eliminare il cliente",
        variant: "destructive"
      });
      return false;
    }
  };

  // Implementiamo la funzione mancante checkDatabaseIntegrity
  const checkDatabaseIntegrity = useCallback(async () => {
    try {
      // Importiamo la funzione dal modulo clients
      const { checkDatabaseIntegrity } = await import("@/lib/clients");
      return await checkDatabaseIntegrity();
    } catch (error) {
      console.error("Errore durante la verifica dell'integrità del database:", error);
      toast({
        title: "Errore diagnostico",
        description: "Si è verificato un errore durante il controllo dell'integrità del database",
        variant: "destructive"
      });
      throw error;
    }
  }, [toast]);

  return {
    filteredClients,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    saveClient,
    deleteClient,
    checkDatabaseIntegrity,
    refreshClients
  };
}
